import { z } from 'zod';
import { bdtAmountSchema, labelledUuid, uuidSchema } from './common.js';

/**
 * FY start year — e.g. 2026 means FY 2026-27 (1 Jul 2026 → 30 Jun 2027).
 * Range is generous; service-layer guards against absurd values.
 */
const fyYearSchema = z.coerce
  .number({ message: 'FY year is required' })
  .int('FY year must be a whole number')
  .min(2000, 'FY year must be 2000 or later')
  .max(2100, 'FY year must be 2100 or earlier');

const alertThresholdSchema = z.coerce
  .number()
  .int()
  .min(1, 'Threshold must be at least 1%')
  .max(100, 'Threshold cannot exceed 100%');

export const createBudgetRequestSchema = z.object({
  fundId: labelledUuid('fund'),
  /** Null/undefined means a fund-level budget (no sub-head scope). */
  subHeadId: labelledUuid('sub-head').nullable().optional(),
  fyYear: fyYearSchema,
  amount: bdtAmountSchema,
  alertThresholdPercent: alertThresholdSchema.default(90),
});
export type CreateBudgetRequest = z.infer<typeof createBudgetRequestSchema>;

export const updateBudgetRequestSchema = z.object({
  amount: bdtAmountSchema.optional(),
  alertThresholdPercent: alertThresholdSchema.optional(),
});
export type UpdateBudgetRequest = z.infer<typeof updateBudgetRequestSchema>;

export const budgetListQuerySchema = z.object({
  fyYear: fyYearSchema,
  fundId: uuidSchema.optional(),
});
export type BudgetListQuery = z.infer<typeof budgetListQuerySchema>;

export const budgetVsActualQuerySchema = z.object({
  fundId: uuidSchema,
  fyYear: fyYearSchema,
});
export type BudgetVsActualQuery = z.infer<typeof budgetVsActualQuerySchema>;

export interface BudgetResponse {
  id: string;
  fundId: string;
  fundCode: string;
  subHeadId: string | null;
  subHeadCode: string | null;
  subHeadName: string | null;
  fyYear: number;
  fyLabel: string;
  amount: string;
  alertThresholdPercent: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * One row in the budget-vs-actual report: budgeted amount, actual DEBIT spend
 * inside the FY, and derived utilisation flags. Fund-level budget rows roll
 * up every DEBIT in the fund regardless of sub-head.
 */
export interface BudgetVsActualRow {
  budgetId: string;
  fundId: string;
  fundCode: string;
  subHeadId: string | null;
  subHeadCode: string | null;
  subHeadName: string | null;
  fyYear: number;
  fyLabel: string;
  budgetAmount: string;
  actualSpend: string;
  /** Spend ÷ Budget × 100, rounded to 2dp. Capped at 0 when budget is 0. */
  utilisationPercent: number;
  /** Budget − Spend. Negative when overspent. */
  remaining: string;
  alertThresholdPercent: number;
  /** utilisationPercent >= alertThresholdPercent */
  isBreached: boolean;
  /** actualSpend > budgetAmount */
  isOverspent: boolean;
}

/**
 * One month in a budget burndown series. The FY runs 1 Jul → 30 Jun, so a
 * full series has 12 points: Jul, Aug, …, Jun. `cumulativeActual` is the
 * running sum of DEBIT spend through the END of `month` (Asia/Dhaka
 * calendar). `cumulativeBudget` is the linear-burn reference line —
 * budget × (monthIndex + 1) / 12 — so it lands on the full budget at the
 * end of June.
 */
export interface BudgetBurndownPoint {
  /** YYYY-MM (Asia/Dhaka calendar), e.g. "2026-07". */
  month: string;
  cumulativeActual: string;
  cumulativeBudget: string;
}

export interface BudgetBurndownResponse {
  budgetId: string;
  fundId: string;
  fundCode: string;
  subHeadId: string | null;
  subHeadCode: string | null;
  subHeadName: string | null;
  fyYear: number;
  fyLabel: string;
  budgetAmount: string;
  points: BudgetBurndownPoint[];
}
